import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Calendar, Download, Search, ChevronDown, ChevronRight, Users, Loader2 } from 'lucide-react';
import { API_URL } from '../utils/config';


const UnlinkedBookings = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearBookings, setYearBookings] = useState([]);
  const [loadingYear, setLoadingYear] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedYears, setExpandedYears] = useState([]);

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/unlinked-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
        // Auto-expand current and next year
        const currentYear = new Date().getFullYear();
        setExpandedYears([currentYear, currentYear + 1]);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchYearBookings = useCallback(async (year) => {
    setLoadingYear(true);
    setSelectedYear(year);
    try {
      const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/unlinked-bookings?year=${year}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setYearBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching year bookings:', error);
    } finally {
      setLoadingYear(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const toggleYear = (year) => {
    if (expandedYears.includes(year)) {
      setExpandedYears(expandedYears.filter(y => y !== year));
      if (selectedYear === year) {
        setSelectedYear(null);
        setYearBookings([]);
      }
    } else {
      setExpandedYears([...expandedYears, year]);
      fetchYearBookings(year);
    }
  };

  const handleExport = (year) => {
    const token = localStorage.getItem('hw_token') || localStorage.getItem('token');
    window.open(`${API_URL}/api/admin/unlinked-bookings/export/${year}?token=${token}`, '_blank');
  };

  const filteredBookings = yearBookings.filter(b => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (b.booking_code || '').toLowerCase().includes(search) ||
      (b.guest_name || '').toLowerCase().includes(search) ||
      (b.property_name || '').toLowerCase().includes(search) ||
      (b.property_prefix || '').includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Prenotazioni da Associare</h2>
            <p className="text-sm text-slate-500">
              Prenotazioni non collegate a nessuna proprietà nel sistema
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-amber-600">{summary?.total_unlinked || 0}</div>
          <div className="text-sm text-slate-500">totali</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          Queste prenotazioni hanno un <strong>property_code</strong> che non corrisponde a nessuna 
          proprietà nel database. Per associarle, è necessario creare/importare le proprietà mancanti.
        </p>
      </div>

      {/* Years List */}
      <div className="bg-white rounded-lg border border-slate-200 divide-y">
        {summary?.by_year?.map((yearData) => {
          const isExpanded = expandedYears.includes(yearData.year);
          const isCurrentYear = yearData.year === new Date().getFullYear();
          const isFutureYear = yearData.year > new Date().getFullYear();
          
          return (
            <div key={yearData.year}>
              {/* Year Header */}
              <div 
                className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 ${
                  isFutureYear ? 'bg-red-50' : isCurrentYear ? 'bg-amber-50' : ''
                }`}
                onClick={() => toggleYear(yearData.year)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <Calendar className={`w-5 h-5 ${isFutureYear ? 'text-red-500' : isCurrentYear ? 'text-amber-500' : 'text-slate-400'}`} />
                  <span className="font-semibold text-slate-800">{yearData.year}</span>
                  {isFutureYear && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      URGENTE
                    </span>
                  )}
                  {isCurrentYear && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                      Anno corrente
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className={`font-bold ${isFutureYear ? 'text-red-600' : 'text-slate-700'}`}>
                      {yearData.count}
                    </span>
                    <span className="text-slate-500 text-sm ml-1">prenotazioni</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    {yearData.unique_properties} proprietà
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleExport(yearData.year); }}
                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Esporta CSV"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && selectedYear === yearData.year && (
                <div className="p-4 bg-slate-50 border-t">
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Cerca per codice, ospite o proprietà..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  {loadingYear ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Prefisso</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Codice Prenotazione</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Nome Proprietà</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Ospite</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Check-in</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Check-out</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Ospiti</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {filteredBookings.map((booking, idx) => (
                            <tr key={idx} className="hover:bg-white">
                              <td className="px-3 py-2">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-mono rounded">
                                  {booking.property_prefix || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{booking.booking_code}</td>
                              <td className="px-3 py-2 text-slate-600 truncate max-w-[200px]" title={booking.property_name}>
                                {booking.property_name || '-'}
                              </td>
                              <td className="px-3 py-2">{booking.guest_name || '-'}</td>
                              <td className="px-3 py-2">{booking.check_in}</td>
                              <td className="px-3 py-2">{booking.check_out}</td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Users className="w-3 h-3 text-slate-400" />
                                  {booking.guests_count || 0}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {filteredBookings.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          Nessuna prenotazione trovata
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
        <h4 className="font-medium mb-2">Come associare le prenotazioni:</h4>
        <ol className="list-decimal list-inside space-y-1">
          <li>Identifica il <strong>prefisso</strong> della proprietà (es. 1358 = ILA1358)</li>
          <li>Verifica se la proprietà esiste nel sistema o va creata</li>
          <li>Se la proprietà esiste con codice diverso, aggiorna il <code>property_code</code></li>
          <li>Esegui il fix automatico dalla console admin</li>
        </ol>
      </div>
    </div>
  );
};

export default UnlinkedBookings;
